import React, {useState} from "react";
import { Table } from "antd";
import { useSelector } from "react-redux";
import commaNumber from "comma-number";

const format = commaNumber.bindWith(",", ".");

const SupplierDueTable = ({ printMode = false, authInfo }) => {
  const { list, loading, filters, totalBalance } = useSelector((state) => state.supplierDue);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };
  


  const columns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "Supplier Name", dataIndex: "acc_name" },
    { title: "Contact No", dataIndex: "contact_no" },
    { title: "Address", dataIndex: "address" },
    { 
      title: "Opening", 
      dataIndex: "opening_balance",
      render: (val) => format(parseFloat(val || 0).toFixed(2)),
      align: 'right'
    },
    { 
      title: "Bill Amount", 
      dataIndex: "total_bill_amount",
      render: (val) => format(parseFloat(val || 0).toFixed(2)),
      align: 'right'
    },
    { 
      title: "Payment", 
      dataIndex: "total_pay_amount",
      render: (val) => format(parseFloat(val || 0).toFixed(2)),
      align: 'right'
    },
    { 
      title: "Received", 
      dataIndex: "received_total",
      render: (val) => format(parseFloat(val || 0).toFixed(2)),
      align: 'right'
    },
    { 
      title: "Return", 
      dataIndex: "return_amount",
      render: (val) => format(parseFloat(val || 0).toFixed(2)),
      align: 'right'
    },
    { 
      title: "Balance", 
      dataIndex: "balance",
      render: (val) => format(parseFloat(val || 0).toFixed(2)),
      align: 'right'
    },
  ];

  const calculateTotals = () => {
    return list.reduce((acc, row) => ({
      opening: acc.opening + parseFloat(row.opening_balance || 0),
      billAmount: acc.billAmount + parseFloat(row.total_bill_amount || 0),
      payment: acc.payment + parseFloat(row.total_pay_amount || 0),
      received: acc.received + parseFloat(row.received_total || 0),
      return: acc.return + parseFloat(row.return_amount || 0),
    }), {
      opening: 0, billAmount: 0, payment: 0, received: 0, return: 0
    });
  };

  const totals = calculateTotals();
  if (printMode) {
  return (
    <table className="ledger-print-table" style={{
    tableLayout: "fixed",
    width: "100%",
  }}
>
      <colgroup>
        <col style={{ width: "4%" }} />
        <col style={{ width: "18%" }} />
        <col style={{ width: "15%" }} />
        <col style={{ width: "18%" }} />
        <col style={{ width: "8%" }} />
        <col style={{ width: "9%" }} />
        <col style={{ width: "9%" }} />
        <col style={{ width: "9%" }} />
        <col style={{ width: "9%" }} />
        <col style={{ width: "10%" }} />
      </colgroup>

      <tbody>
        <tr className="ledger-print-heading">
          <td>No.</td>
          <td>Supplier Name</td>
          <td>Contact No</td>
          <td>Address</td>
          <td style={{ textAlign: "right" }}>Opening</td>
          <td style={{ textAlign: "right" }}>Amount</td>
          <td style={{ textAlign: "right" }}>Payment</td>
          <td style={{ textAlign: "right" }}>Received</td>
          <td style={{ textAlign: "right" }}>Return</td>
          <td style={{ textAlign: "right" }}>Balance</td>
        </tr>

        {list.map((row, index) => (
          <tr key={`${row.acc_id}-${index}`}>
            <td>{index + 1}</td>
            <td>{row.acc_name}</td>
            <td>{row.contact_no}</td>
            <td
                style={{
    maxWidth: "150px",
    wordBreak: "break-word",
    whiteSpace: "normal",
  }}
            >{row.address}</td>

            <td style={{ textAlign: "right" }}>
              {format(parseFloat(row.opening_balance || 0).toFixed(2))}
            </td>

            <td style={{ textAlign: "right" }}>
              {format(parseFloat(row.total_bill_amount || 0).toFixed(2))}
            </td>

            <td style={{ textAlign: "right" }}>
              {format(parseFloat(row.total_pay_amount || 0).toFixed(2))}
            </td>

            <td style={{ textAlign: "right" }}>
              {format(parseFloat(row.received_total || 0).toFixed(2))}
            </td>

            <td style={{ textAlign: "right" }}>
              {format(parseFloat(row.return_amount || 0).toFixed(2))}
            </td>

            <td style={{ textAlign: "right" }}>
              {format(parseFloat(row.balance || 0).toFixed(2))}
            </td>
          </tr>
        ))}

        <tr className="ledger-total-row">
          <td colSpan={4} style={{ textAlign: "right" }}>
            Grand Total
          </td>

          <td style={{ textAlign: "right" }}>
            {format(totals.opening.toFixed(2))}
          </td>

          <td style={{ textAlign: "right" }}>
            {format(totals.billAmount.toFixed(2))}
          </td>

          <td style={{ textAlign: "right" }}>
            {format(totals.payment.toFixed(2))}
          </td>

          <td style={{ textAlign: "right" }}>
            {format(totals.received.toFixed(2))}
          </td>

          <td style={{ textAlign: "right" }}>
            {format(totals.return.toFixed(2))}
          </td>

          <td style={{ textAlign: "right" }}>
            {format(totalBalance.toFixed(2))}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

  return (
    <Table
      rowKey={(record, index) => `${record.acc_id}-${index}`}
      columns={columns}
      dataSource={list}
      loading={loading}
      pagination={{
        ...pagination,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50"],
      }}
      onChange={handleTableChange}
      bordered
      scroll={printMode ? undefined : { x: 'max-content' }}
      size={printMode ? "small" : "middle"}
      summary={() => (
        <Table.Summary.Row>
          <Table.Summary.Cell index={0} colSpan={4}>
            <strong>Grand Total</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={1} align="right">
            <strong>{format(totals.opening.toFixed(2))}</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={2} align="right">
            <strong>{format(totals.billAmount.toFixed(2))}</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={3} align="right">
            <strong>{format(totals.payment.toFixed(2))}</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={4} align="right">
            <strong>{format(totals.received.toFixed(2))}</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={5} align="right">
            <strong>{format(totals.return.toFixed(2))}</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={6} align="right">
            <strong>{format(totalBalance.toFixed(2))}</strong>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      )}
    />
  );
};

export default SupplierDueTable;